import { Text } from '@components/primitives/Text'
import { getPhaseFromPhaseList } from '@modules/trials/helpers/getPhaseFromPhaseList'
import { getPrimaryConditionFromTrial } from '@modules/trials/helpers/getPrimaryConditionFromTrial'
import { type TrialAssociation } from '@modules/trials/types/TrialAssociation'
import type { ImageCategory } from '@prisma/client'

export const TrialMediaLibraryDescription = ({
  category,
  trial,
}: {
  category: ImageCategory
  trial: TrialAssociation
}) => {
  const primaryCondition = getPrimaryConditionFromTrial(trial)
  const armGroupNames = trial.armGroups?.map((armGroup) => armGroup.label)
  const trialPhase = getPhaseFromPhaseList(trial.phaseList)

  switch (category) {
    case 'TrialPatientQuotes':
      return (
        <div>{`${primaryCondition} Patient Testimony for trial: Trial Name: ${trial.nctId} — ${trialPhase}`}</div>
      )
    case 'TrialEligibility':
      return (
        <div>
          <Text element='div' styleName='h4'>
            Eligibility Criteria:
          </Text>
          <Text styleName='p'>
            This trial includes the following eligibility criteria:
          </Text>
          <ul className='pt-2'>
            {trial.parsedEligibilityCriteria?.map(
              ({ assertiveSummary }, index) =>
                assertiveSummary && (
                  <Text
                    className='text-neutral600'
                    key={index}
                    styleName='p-small'
                    value={assertiveSummary}
                  ></Text>
                ),
            )}
          </ul>
        </div>
      )
    case 'TrialAwards':
      return (
        <div>
          <Text element='div' styleName='h4'>
            Awards:
          </Text>
          <Text styleName='p'>
            This trial has {trial.awardBadges?.length} awards, including:
          </Text>
          <ul className='pt-2'>
            {trial.awardBadges?.map((badge, index) => (
              <li key={index}>
                <Text className='text-neutral600' styleName='p-small'>
                  <em>{badge.name}</em> - {badge.whyThisMatters}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      )
    case 'TrialTimeline':
      return (
        <div>
          <Text element='div' styleName='h4'>
            Timeline:
          </Text>
          <Text styleName='p'>This trial has the following timeline:</Text>
          <ul className='pt-2'>
            <li>
              <Text className='text-neutral600' styleName='p-small'>
                <em>Screening:</em> It may take up to{' '}
                {trial.timeline?.screeningDuration ?? 3}{' '}
                {trial.timeline?.screeningDurationUnit ?? 'Weeks'} to process to
                see if you qualify in this trial.
              </Text>
            </li>
            <li>
              <Text className='text-neutral600' styleName='p-small'>
                <em>Treatment:</em>{' '}
                {trial.timeline?.treatmentDuration
                  ? 'You will receive the treatment for ' +
                    trial.timeline?.treatmentDuration +
                    ' ' +
                    trial.timeline?.treatmentDurationUnit
                  : 'The duration you will receive the treatment varies.'}
              </Text>
            </li>
            <li>
              <Text className='text-neutral600' styleName='p-small'>
                <em>Follow Ups: </em> You may be asked to continue sharing
                information regarding the trial for{' '}
                {trial.timeline?.followupDuration ?? '6'}{' '}
                {trial.timeline?.followupDurationUnit ?? 'Months'} after you
                stop receiving the treatment.
              </Text>
            </li>
          </ul>
        </div>
      )
    case 'TreatmentDoughnutChart':
      return (
        <div>
          <Text element='div' styleName='h4'>
            Research Study Groups:
          </Text>
          <Text styleName='p'>This trial has the following groups:</Text>
          <ul className='pt-2'>
            {armGroupNames?.map((name, index) => (
              <li key={index}>
                <Text className='text-neutral600' styleName='p-small'>
                  <em>Group {index + 1}:</em> {name}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      )
    default:
      return <> </>
  }
}
